import React, { useMemo, useEffect, useState } from 'react';
import _ from 'lodash';
import { TextField, Typography, Icon, Autocomplete, Tabs } from '@scale/scaleui';
import { EmailTemplateData, EmailGuardrailSuggestion } from '@scale/shared-types';
import AvailableVariablesList from './AvailableVariablesList';
import { RichTextEditor } from 'design-system';
import TemplatePreview from './TemplatePreview';
import TemplateVariablesList from './TemplateVariablesList';
import ContentSuggestions from './ContentSuggestions';
import { twMerge } from 'tailwind-merge';
import {
  EmailEditorTabOptions,
  EmailPreviewTabOptions,
} from 'react-scale/components/GrowthCenter/EmailDripCampaign/CampaignSetup/EmailTemplateModalV2/EmailTemplateModalV2';

const replaceVariables = (text: string, variableValues: Record<string, string>) => {
  let formattedText = text;

  // TODO: Make handlebars compiling logic to library, so it can be shared between front and backend
  Object.keys(variableValues).forEach(variable => {
    if (variableValues[variable]) {
      formattedText = formattedText.replaceAll(`{{${variable}}}`, variableValues[variable]);
    }
  });

  return formattedText;
};

const APPROVED_SENDER_EMAILS = [
  'admin@outlier.ai',
  'no-reply@outlier.ai',
  'accountaccess@outlier.ai (connected to zendesk)',
  'charles.farr@outlier.ai (connected to zendesk)',
  'accountaccess@remotasks.com (connected to zendesk)',
  'specialists-recruiting@outlier.ai (connected to zendesk)',
  'languages-recruiting@outlier.ai',
  'onboarding@outlier.ai (connected to zendesk)',
  'ross@outlier.ai',
  'quintin@outlier.ai',
  'no-reply@remotasks.com',
  'humanfrontiercollective@scale.com',
  'no-reply.hfc@scale.com',
  'outreach@outlier.ai',
];
interface EmailEditorProps {
  selectedTemplate: EmailTemplateData;
  availableVariables: string[];
  templateVariables: string[];
  userId?: string;
  body: string;
  subject: string;
  senderName: string;
  senderEmail: string;
  variableValues: Record<string, string>;
  setBody: (val: string) => void;
  setSubject: (val: string) => void;
  setSenderName: (val: string) => void;
  setSenderEmail: (val: string) => void;
  setVariableValues: (val: Record<string, string>) => void;
  onChangeTemplate: () => void;
  isValidEmail: boolean;
  setIsValidEmail: (val: boolean) => void;
  isViewOnly?: boolean;
  emailEditorTabs: { id: string; label: string }[];
  emailPreviewTabs: { id: string; label: string }[];
  previewTab: EmailPreviewTabOptions;
  setPreviewTab: React.Dispatch<React.SetStateAction<EmailPreviewTabOptions>>;
  validationSuggestions?: EmailGuardrailSuggestion[];
  isLoadingEmailContentValidation?: boolean;
}

const EmailEditor = ({
  selectedTemplate,
  availableVariables,
  templateVariables,
  userId,
  body,
  subject,
  senderName,
  senderEmail,
  variableValues,
  setBody,
  setSubject,
  setSenderName,
  setSenderEmail,
  setVariableValues,
  onChangeTemplate,
  isValidEmail,
  setIsValidEmail,
  isViewOnly = false,
  emailEditorTabs,
  emailPreviewTabs,
  previewTab,
  setPreviewTab,
  validationSuggestions,
  isLoadingEmailContentValidation,
}: EmailEditorProps) => {
  const [tab, setTab] = useState(emailEditorTabs[0].id);
  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(today.getDate()).padStart(2, '0')}`;
  const uploadImgBase = `upload/communication-center/${formattedDate}/${userId}`;

  const previewBody = useMemo(() => replaceVariables(body, variableValues), [body, variableValues]);

  const previewSubject = useMemo(
    () => replaceVariables(subject, variableValues),
    [subject, variableValues],
  );

  const handlePreviewTabChange = (value: string) => {
    setPreviewTab(value as EmailPreviewTabOptions);
  };

  useEffect(() => {
    if (senderEmail) {
      setIsValidEmail(
        !!senderEmail.match(/^[a-zA-Z0-9._+-]+@(remotasks\.com|outlier\.ai)$/) ||
          APPROVED_SENDER_EMAILS.includes(senderEmail),
      );
    }
  }, [senderEmail]);

  return (
    <div className="h-full gap-4 flex flex-col">
      <div className="flex items-center gap-2">
        <Typography.Paragraph2 className="text-neutral-600 mr-4">Template</Typography.Paragraph2>
        <Typography.Paragraph2>
          {selectedTemplate.name} ({selectedTemplate.templateId})
        </Typography.Paragraph2>
        <Icon
          icon={['fas', 'pen']}
          size="sm"
          variant="neutral"
          tooltip="Change template"
          disabled={isViewOnly}
          onClick={onChangeTemplate}
        />
      </div>
      <div className="flex w-full flex-grow gap-4 h-full">
        <div className="relative w-1/2 h-full ">
          <div className="absolute overflow-auto flex flex-col gap-2 h-full w-full">
            {!_.isEmpty(templateVariables) && (
              <Tabs onChange={setTab} tabs={emailEditorTabs} value={tab} />
            )}
            {!_.isEmpty(templateVariables) && tab === EmailEditorTabOptions.TemplateVariables ? (
              <TemplateVariablesList
                templateVariables={templateVariables}
                variableValues={variableValues}
                setVariableValues={setVariableValues}
              />
            ) : (
              <>
                <div className="flex items-start gap-4">
                  <TextField
                    className="flex-1"
                    value={senderName}
                    onChange={e => setSenderName(e.target.value)}
                    label="Sender name"
                    error={!senderName}
                    disabled={isViewOnly}
                  />
                  <div className="flex-1">
                    <Autocomplete
                      label="Sender email"
                      options={APPROVED_SENDER_EMAILS}
                      value={senderEmail}
                      setSelectedOption={(value: string) => {
                        setSenderEmail(value.split(' (connected to zendesk)')[0]);
                      }}
                      disabled={isViewOnly}
                    />
                    <Typography.Paragraph3
                      className={twMerge('text-danger-500', isValidEmail && 'invisible')}
                    >
                      Please use @remotasks.com or @outlier.ai emails
                    </Typography.Paragraph3>
                  </div>
                </div>

                <TextField
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  label="Email subject"
                  error={!subject}
                  disabled={isViewOnly}
                />

                <AvailableVariablesList availableVariables={availableVariables} />

                {!isViewOnly && (
                  <div className="relative flex-grow min-h-[480px]">
                    <RichTextEditor
                      className="absolute h-full"
                      value={body}
                      onChange={val => setBody(val)}
                      options={{
                        uploadImgBase,
                      }}
                      enableGrammarChecking
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="relative w-1/2 h-full flex flex-col gap-2">
          <Tabs onChange={handlePreviewTabChange} tabs={emailPreviewTabs} value={previewTab} />
          {previewTab === EmailPreviewTabOptions.PreviewContent ? (
            <>
              <Typography.Paragraph2 className="inline-block">
                Subject: {previewSubject}
              </Typography.Paragraph2>
              <div className="relative flex-grow">
                <TemplatePreview
                  className="absolute"
                  templateHTML={selectedTemplate.htmlWrapper}
                  content={previewBody}
                  subject={previewSubject}
                  sendgridTemplateId={selectedTemplate.sendgridTemplateId}
                />
              </div>
            </>
          ) : (
            <div className="relative flex-grow">
              <ContentSuggestions
                suggestions={validationSuggestions ?? []}
                isLoadingEmailContentValidation={isLoadingEmailContentValidation}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailEditor;
